import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import {
  TechnologyCatalogItem,
  TechnologyCatalogItemUpsertPayload,
  createTechnologyCatalogItem,
  deleteTechnologyCatalogItem,
  listTechnologyCatalogItems,
  updateTechnologyCatalogItem,
} from '../../services/technologyApi';
import {
  TECHNOLOGY_CATALOG_CATEGORY_OPTIONS,
  technologyOptionLabel,
} from './technologyFormOptions';

const PAGE_SIZE = 10;

const emptyForm: TechnologyCatalogItemUpsertPayload = {
  name: '',
  category: 'Software',
  version: '',
  vendor: '',
  licenseType: '',
  notes: '',
};

export function TechnologyCatalogPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('technology.catalog');
  const [items, setItems] = useState<TechnologyCatalogItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TechnologyCatalogItemUpsertPayload>(emptyForm);
  const [saving, setSaving] = useState(false);
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } =
    useCollapsibleItemForm();
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const result = await listTechnologyCatalogItems({
        page: targetPage,
        pageSize: PAGE_SIZE,
        search: search.trim() || undefined,
        category: categoryFilter || undefined,
      });
      setItems(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
    } catch {
      toast.error('Falha ao carregar inventário tecnológico.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, categoryFilter]);

  function resetForm(): void {
    setEditingId(null);
    setForm(emptyForm);
  }

  function startEdit(item: TechnologyCatalogItem): void {
    expandForm();
    setEditingId(item.id);
    setForm({
      name: item.name,
      category: item.category,
      version: item.version ?? '',
      vendor: item.vendor ?? '',
      licenseType: item.licenseType ?? '',
      notes: item.notes ?? '',
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    const payload: TechnologyCatalogItemUpsertPayload = {
      ...form,
      version: form.version?.trim() || null,
      vendor: form.vendor?.trim() || null,
      licenseType: form.licenseType?.trim() || null,
      notes: form.notes?.trim() || null,
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateTechnologyCatalogItem(editingId, payload);
        toast.success('Item atualizado.');
        collapseForm(resetForm);
      } else {
        await createTechnologyCatalogItem(payload);
        toast.success('Item criado.');
        handleCreateSuccess(resetForm);
      }
      await loadData(page);
    } catch {
      toast.error('Falha ao salvar item do inventário.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: TechnologyCatalogItem): Promise<void> {
    if (!canWrite) return;
    const accepted = await confirm({
      title: 'Excluir item',
      message: `Deseja excluir "${item.name}" do inventário?`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!accepted) return;

    try {
      await deleteTechnologyCatalogItem(item.id);
      toast.success('Item excluído.');
      if (editingId === item.id) collapseForm(resetForm);
      await loadData(page);
    } catch {
      toast.error('Falha ao excluir item.');
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Inventário Tecnológico"
        description="Catálogo único das tecnologias utilizadas pela organização."
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar item' : 'Novo item'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={
            editingId
              ? `Editando ${form.name || 'item selecionado'} - clique para expandir`
              : undefined
          }
          createSummary="Clique para expandir e cadastrar uma tecnologia"
          isEditing={!!editingId}
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3">
            <div className="erp-form-grid">
              <label className="erp-label">
                Nome
                <input
                  className="erp-input"
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                  required
                />
              </label>
              <label className="erp-label">
                Categoria
                <select
                  className="erp-input"
                  value={form.category}
                  onChange={(event) => setForm({ ...form, category: event.target.value })}
                >
                  {TECHNOLOGY_CATALOG_CATEGORY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Versão
                <input
                  className="erp-input"
                  value={form.version ?? ''}
                  onChange={(event) => setForm({ ...form, version: event.target.value })}
                />
              </label>
              <label className="erp-label">
                Fornecedor
                <input
                  className="erp-input"
                  value={form.vendor ?? ''}
                  onChange={(event) => setForm({ ...form, vendor: event.target.value })}
                />
              </label>
              <label className="erp-label">
                Licença
                <input
                  className="erp-input"
                  value={form.licenseType ?? ''}
                  onChange={(event) => setForm({ ...form, licenseType: event.target.value })}
                />
              </label>
            </div>
            <label className="erp-label mt-3 block">
              Observações
              <textarea
                className="erp-input mt-1 min-h-[60px]"
                value={form.notes ?? ''}
                onChange={(event) => setForm({ ...form, notes: event.target.value })}
              />
            </label>
            <FormActionBar
              onCancel={() => collapseForm(resetForm)}
              cancelLabel="Cancelar"
              className="erp-btn-actions mt-4"
            >
              <button type="submit" disabled={saving} className="erp-btn-primary">
                {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      <div className="mb-3 flex flex-wrap gap-2">
        <input
          className="erp-input max-w-xs"
          placeholder="Buscar por nome ou fornecedor"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              setPage(1);
              void loadData(1);
            }
          }}
        />
        <select
          className="erp-input max-w-[200px]"
          value={categoryFilter}
          onChange={(event) => {
            setPage(1);
            setCategoryFilter(event.target.value);
          }}
        >
          <option value="">Todas as categorias</option>
          {TECHNOLOGY_CATALOG_CATEGORY_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="erp-btn-secondary"
          onClick={() => {
            setPage(1);
            void loadData(1);
          }}
        >
          Buscar
        </button>
      </div>

      {loading ? (
        <p className="text-secondary-500">Carregando...</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-secondary-200 bg-white">
            <table className="erp-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Categoria</th>
                  <th>Versão</th>
                  <th>Fornecedor</th>
                  <th>Licença</th>
                  {canWrite ? <th className="w-24">Ações</th> : null}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={canWrite ? 6 : 5} className="text-center text-secondary-500">
                      Nenhum item cadastrado.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      <td>
                        {technologyOptionLabel(TECHNOLOGY_CATALOG_CATEGORY_OPTIONS, item.category)}
                      </td>
                      <td>{item.version || '—'}</td>
                      <td>{item.vendor || '—'}</td>
                      <td>{item.licenseType || '—'}</td>
                      {canWrite ? (
                        <td>
                          <div className="flex gap-1">
                            <EditIconButton onClick={() => startEdit(item)} label="Editar" />
                            <DeleteIconButton
                              onClick={() => void handleDelete(item)}
                              label="Excluir"
                            />
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalCount > PAGE_SIZE ? (
            <ListPaginationBar
              totalCount={totalCount}
              page={page}
              pageSize={PAGE_SIZE}
              onPrevious={() => setPage((current) => Math.max(1, current - 1))}
              onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
            />
          ) : null}
        </>
      )}
    </PageShell>
  );
}
